import React, { useEffect, useMemo, useRef, useState } from "react";
import { collection, endAt, limit, onSnapshot, orderBy, query, startAt } from "firebase/firestore";
import { off, onValue, ref } from "firebase/database";
import { database, firestore } from "../../firebaseConfig";
import { getParticipantIdFromThread } from "./helpers";

const fallbackName = (id) => id || "Unknown";

const toMillis = (value) => {
  if (!value) return 0;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.toDate === "function") {
    const d = value.toDate();
    return d?.getTime?.() || 0;
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
};

const getMessagePreview = (data) => {
  if (!data) return "No messages yet";
  if (data.text) return data.text;
  if (data.file?.fileName) return `📎 ${data.file.fileName}`;
  return "No messages yet";
};

const ThreadList = ({
  companyName,
  participantType = "participant",
  selectedParticipantId,
  onSelect,
}) => {
  const [threads, setThreads] = useState([]);
  const [nameMap, setNameMap] = useState({});
  const [loading, setLoading] = useState(false);
  const messageUnsubsRef = useRef({});
  const lastThreadKeyRef = useRef("");

  // Load participant display names from RTDB (farmers or clients)
  useEffect(() => {
    if (!companyName || !database) return undefined;
    const path =
      participantType === "farmer"
        ? `Companies/${companyName}/ValidatedFarmers`
        : `Companies/${companyName}/ValidatedClients`;
    const r = ref(database, path);
    // Company data stores names under a few variants (CloudName/Name/OtherName) because
    // farmers and clients are created from different flows. Prefer CloudName, then fallbacks.
    const handleValue = (snap) => {
      const val = snap.val() || {};
      const mapping = {};
      Object.keys(val).forEach((key) => {
        const node = val[key] || {};
        mapping[key] =
          node.CloudName || node.cloudName || node.Name || node.name || node.OtherName || key;
      });
      setNameMap(mapping);
    };
    onValue(r, handleValue, (err) => {
      // eslint-disable-next-line no-console
      console.error("Failed to load participant names:", companyName, participantType, err);
    });
    return () => off(r, handleValue);
  }, [companyName, participantType]);

  // Subscribe to thread list
  useEffect(() => {
    if (!companyName || !firestore) return undefined;
    setLoading(true);
    const prefix = `${companyName}__`;
    const threadsQuery = query(
      collection(firestore, "companyMessages"),
      orderBy("__name__"),
      startAt(prefix),
      endAt(`${prefix}\uf8ff`)
    );
    const unsub = onSnapshot(
      threadsQuery,
      (snap) => {
        const next = [];
        snap.forEach((d) => {
          const participantId = getParticipantIdFromThread(d.id, companyName);
          if (!participantId) return;
          const data = d.data() || {};
          if (data.archivedForCompany) return;
          next.push({
            threadId: d.id,
            participantId,
          });
        });
        setThreads(next);
        setLoading(false);
      },
      (err) => {
        // eslint-disable-next-line no-console
        console.error("Failed to load message threads for company", companyName, err);
        setLoading(false);
      }
    );
    return () => {
      unsub();
    };
  }, [companyName]);

  const allowedParticipantIds = useMemo(
    () => new Set(Object.keys(nameMap || {})),
    [nameMap]
  );

  const visibleThreads = useMemo(() => {
    if (!allowedParticipantIds.size) return [];
    return threads.filter((t) => allowedParticipantIds.has(t.participantId));
  }, [threads, allowedParticipantIds]);

  const threadIds = useMemo(() => {
    return Array.from(new Set(visibleThreads.map((t) => t.threadId)));
  }, [visibleThreads]);

  const threadIdsKey = useMemo(() => threadIds.join("|"), [threadIds]);

  // Subscribe to latest message per thread for preview/unread marker
  useEffect(() => {
    if (!firestore) return undefined;

    // cleanup previous listeners
    Object.values(messageUnsubsRef.current).forEach((fn) => fn());
    messageUnsubsRef.current = {};

    lastThreadKeyRef.current = threadIdsKey;

    threadIds.forEach((threadId) => {
      const q = query(
        collection(firestore, "companyMessages", threadId, "messages"),
        orderBy("createdAt", "desc"),
        limit(1)
      );
      const unsub = onSnapshot(
        q,
        (snap) => {
          const docSnap = snap.docs?.[0];
          const data = docSnap?.data();
          setThreads((prev) =>
            prev.map((item) =>
              item.threadId === threadId
                ? {
                    ...item,
                    lastMessage: getMessagePreview(data),
                    lastSender: data?.sender,
                    lastCreatedAt: data?.createdAt,
                    unread:
                      data?.sender !== "company" &&
                      data?.readByCompany !== true &&
                      !data?.deletedForCompany,
                  }
                : item
            )
          );
        },
        (err) => {
          // eslint-disable-next-line no-console
          console.error("Failed to subscribe to latest message for thread", threadId, err);
        }
      );
      messageUnsubsRef.current[threadId] = unsub;
    });

    return () => {
      Object.values(messageUnsubsRef.current).forEach((fn) => fn());
      messageUnsubsRef.current = {};
    };
  }, [threadIdsKey, firestore]);

  const sortedThreads = useMemo(() => {
    return [...visibleThreads].sort((a, b) => {
      const aTime = toMillis(a.lastCreatedAt);
      const bTime = toMillis(b.lastCreatedAt);
      if (bTime === aTime) {
        return (b.participantId || "").localeCompare(a.participantId || "");
      }
      return bTime - aTime;
    });
  }, [visibleThreads]);

  if (!companyName) {
    return null;
  }

  return (
    <div className="w-full sm:w-72 border border-slate-200 rounded-2xl bg-slate-50/80 backdrop-blur-sm p-3 space-y-2">
      <div className="flex items-center justify-between text-xs text-slate-600">
        <span className="font-medium text-black dark:text-orange-300">Recent chats</span>
        {loading && <span className="animate-pulse">Loading…</span>}
      </div>
      {sortedThreads.length === 0 && (
        <div className="text-xs text-slate-500 py-4 text-center">
          No conversations yet. New chats will appear automatically.
        </div>
      )}
      <div className="space-y-2 max-h-72 overflow-y-auto">
        {sortedThreads.map((t) => {
          const name = nameMap[t.participantId] || fallbackName(t.participantId);
          return (
            <button
              key={t.threadId}
              type="button"
              onClick={() => onSelect?.(t.participantId)}
              className={`w-full text-left px-3 py-2 rounded-lg border ${
                selectedParticipantId === t.participantId
                  ? "border-emerald-300 bg-white shadow-sm"
                  : "border-transparent bg-white/95 hover:border-emerald-100"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="font-semibold text-sm text-black dark:text-orange-300 truncate">{name}</div>
                {t.unread && (
                  <span className="text-[10px] bg-emerald-600 text-white px-2 py-0.5 rounded-full">
                    New
                  </span>
                )}
              </div>
              <div className="text-[11px] text-slate-500 dark:text-orange-300 truncate">
                {t.lastMessage || "No messages yet"}
              </div>
              <div className="text-[10px] text-slate-400 truncate">
                @{t.participantId}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ThreadList;
